import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stack,
  Chip,
  useTheme,
  Dialog,
  TextField,
  CircularProgress,
  IconButton,
  Fade,
  Slide,
  Avatar,
  InputAdornment,
  Grow,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  AlertTitle,
  Snackbar,
  AlertColor,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Icons
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import SparklesIcon from '@mui/icons-material/AutoFixHigh';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GetAppIcon from '@mui/icons-material/GetApp';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import PhoneIcon from '@mui/icons-material/Phone';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { NewReleases } from '@mui/icons-material';

// Components
import AdminNavbar from '../../components/Navbar/AdminNavbar';
import Footer from '../../components/Footer/Footer';
import { useNavigate } from 'react-router-dom';
import { formatName } from 'shared/functions/home';
import axios from 'axios';
import { environment } from '../../../environments/environment';

// Firebase
import { onAuthStateChanged, createUserWithEmailAndPassword, sendEmailVerification, deleteUser } from 'firebase/auth';
import { authen, adminAuth } from '../../firebase/firebase';
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';

const COLORS = {
  primary: '#2e7d32',
  primaryLight: '#4caf50',
  primaryDark: '#1b5e20',
  secondary: '#66bb6a',
  accent: '#81c784',
  success: '#388e3c',
  warning: '#f57c00',
  background: '#f8f9fa',
  surface: '#ffffff',
  text: {
    primary: '#263238',
    secondary: '#546e7a',
    light: '#78909c',
  },
  border: '#e0e0e0',
  hover: '#f1f8e9',
};

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
`;

const ModernHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, #f8fffe 0%, #f0f9f4 100%)`,
  borderRadius: 12,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  border: `1px solid rgba(46, 125, 50, 0.08)`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #2A9235 0%, #4CAF50 100%)',
  },
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: theme.spacing(2),
  textAlign: 'center',
}));

const WelcomeSection = styled(Box)({
  width: '100%',
  textAlign: 'center',
});

const ActionsSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'center',
  },
}));

const CompactButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(0.75, 1.5),
  fontSize: '0.875rem',
  fontWeight: 500,
  textTransform: 'none',
  minHeight: 36,
  backgroundColor: 'rgba(46, 125, 50, 0.08)',
  color: COLORS.primary,
  border: `1px solid rgba(46, 125, 50, 0.15)`,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(46, 125, 50, 0.12)',
    borderColor: COLORS.primary,
    transform: 'translateY(-1px)',
  },
}));

const StatusDot = styled(Box)({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#4CAF50',
  marginRight: 8,
  flexShrink: 0,
});

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const MainContainer = styled(Box)({
  minHeight: '100vh',
  backgroundColor: COLORS.background,
  position: 'relative',
});

const StyledCard = styled(Card)({
  height: '100%',
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  backgroundColor: COLORS.surface,
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  transition: 'all 0.3s cubic-bezier(0.23, 1, 0.32, 1)',

  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 25px rgba(46, 125, 50, 0.15)',
    borderColor: COLORS.primary,
  },
});

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.25, 3),
  fontSize: '1rem',
  fontWeight: 700,
  textTransform: 'none',
  minWidth: '220px',
  minHeight: '48px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
  color: 'white',
  border: 'none',
  boxShadow: '0 6px 18px rgba(42, 146, 53, 0.18)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background:
      'linear-gradient(90deg, transparent, rgba(255,255,255,0.16), transparent)',
    transition: 'left 0.5s ease',
  },
  '&:hover': {
    background: 'linear-gradient(135deg, #1e7a2b 0%, #43a047 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 30px rgba(42, 146, 53, 0.22)',
    '&::before': {
      left: '100%',
    },
  },
  '&:disabled': {
    backgroundColor: COLORS.text.light,
    color: 'white',
    opacity: 0.7,
    transform: 'none',
    boxShadow: 'none',
  },
}));

const SecondaryButton = styled(Button)({
  borderRadius: 6,
  padding: '12px 24px',
  fontSize: '0.95rem',
  fontWeight: 500,
  textTransform: 'none',
  backgroundColor: 'white',
  color: COLORS.primary,
  border: `1px solid ${COLORS.border}`,
  minHeight: 44,
  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: COLORS.hover,
    borderColor: COLORS.primary,
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(46, 125, 50, 0.15)',
  },

  '&:disabled': {
    borderColor: COLORS.border,
    color: COLORS.text.light,
  },
});

const ResponsiveTabsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  maxWidth: '600px',
  margin: '0 auto',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
  },
}));

const ResponsiveStyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: 'rgba(255,255,255,0.95)',
  borderRadius: 999,
  padding: theme.spacing(0.25),
  border: `1px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(42,146,53,0.08)'
      : 'rgba(255,255,255,0.06)'
  }`,
  boxShadow: '0 6px 18px rgba(16,24,40,0.06)',
  minHeight: 48,
  width: '100%',
  '& .MuiTabs-indicator': { display: 'none' },
  '& .MuiTabs-flexContainer': {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

const ResponsiveStyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#4b5563',
  flex: '1',
  minWidth: 'auto',
  maxWidth: 'none',
  padding: theme.spacing(1, 2.5),
  borderRadius: 999,
  margin: theme.spacing(0, 0.25),
  whiteSpace: 'nowrap',
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  minHeight: 44,

  [theme.breakpoints.down('sm')]: {
    fontSize: '0.85rem',
    padding: theme.spacing(0.75, 2),
    minHeight: 40,
  },

  '& .MuiSvgIcon-root': {
    fontSize: '1.05rem',
  },

  '&.Mui-selected': {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 20px rgba(42,146,53,0.12)',
  },

  '&:hover:not(.Mui-selected)': {
    backgroundColor: 'rgba(42,146,53,0.04)',
  },
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: 6,
    backgroundColor: 'white',
    transition: 'all 0.3s ease',

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.secondary,
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.primary,
      borderWidth: '2px',
    },
  },
});

const RecommendedChip = styled(Chip)({
  fontWeight: 600,
  fontSize: '0.75rem',
  borderRadius: 20,
  backgroundColor: 'rgba(245, 124, 0, 0.15)',
  color: COLORS.warning,
  border: `1px solid rgba(245, 124, 0, 0.3)`,
});

const FlexibleChip = styled(Chip)({
  fontWeight: 600,
  fontSize: '0.75rem',
  borderRadius: 20,
  backgroundColor: 'rgba(46, 125, 50, 0.15)',
  color: COLORS.primary,
  border: `1px solid rgba(46, 125, 50, 0.3)`,
});

const InfoCard = styled(Paper)({
  padding: '20px',
  borderRadius: 6,
  backgroundColor: 'rgba(46, 125, 50, 0.05)',
  border: `1px solid rgba(46, 125, 50, 0.1)`,
});

const ModernDialog = styled(Dialog)(({ open }) => ({
  '& .MuiPaper-root': {
    borderRadius: 8,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
    overflow: 'hidden',
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
  },
}));

const Transition = React.forwardRef(function Transition(
  props: any,
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface AdminData {
  username: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

export default function SchoolAdminDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const db = getFirestore();

  // State management
  const [adminName, setAdminName] = useState('Admin');
  const [schoolName, setSchoolName] = useState('Your School');
  const [adminUid, setAdminUid] = useState<string | null>(null);
  const [schoolIdState, setSchoolIdState] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [openGenerateModal, setOpenGenerateModal] = useState(false);
  const [openAddAdminModal, setOpenAddAdminModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [teacherCount, setTeacherCount] = useState(0);
  const [studentCount, setStudentCount] = useState(10);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccessModalOpen, setUploadSuccessModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [adminRole, setAdminRole] = useState('admin');
  const [generatedUsers, setGeneratedUsers] = useState<any[]>([]);
  const [uploadedUsers, setUploadedUsers] = useState<any[]>([]);

  // Admin form state
  const [adminData, setAdminData] = useState<AdminData>({
    username: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor | undefined;
  }>({
    open: false,
    message: '',
    severity: undefined,
  });

  const showSnackbar = (message: string, severity: AlertColor) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(authen, async (currentUser) => {
      if (!currentUser) {
        setAdminName('Admin');
        setSchoolName('Your School');
        return;
      }

      try {
        let caData: any | null = null;
        const caRef = doc(db, 'custom_auth', currentUser.uid);
        const caSnap = await getDoc(caRef);
        if (caSnap.exists()) {
          caData = caSnap.data();
        } else if (currentUser.email) {
          const q = query(
            collection(db, 'custom_auth'),
            where('email', '==', currentUser.email)
          );
          const qs = await getDocs(q);
          if (!qs.empty) caData = qs.docs[0].data();
        }

        setAdminUid(currentUser.uid);

        // Extract and store current user's role
        const userRole = caData?.role || caData?.admin_role || caData?.user_type || 'admin';
        setCurrentUserRole(userRole);

        const caName =
          caData?.username || caData?.full_name || caData?.name || '';
        const authFallback =
          currentUser.displayName ||
          (currentUser.email ? currentUser.email.split('@')[0] : '') ||
          'Admin';
        setAdminName(formatName(caName || authFallback));

        const schoolId =
          caData?.school_id || caData?.schoolId || caData?.schoolID || null;
        setSchoolIdState(schoolId ? String(schoolId) : null);

        if (schoolId) {
          const soRef = doc(db, 'school_onboarded', String(schoolId));
          const soSnap = await getDoc(soRef);
          if (soSnap.exists()) {
            const so = soSnap.data() as any;
            const name = so?.name || so?.school_name || so?.display_name || '';
            setSchoolName(name || 'Your School');
          } else {
            const q2 = query(
              collection(db, 'school_onboarded'),
              where('school_id', '==', String(schoolId))
            );
            const qs2 = await getDocs(q2);
            if (!qs2.empty) {
              const so = qs2.docs[0].data() as any;
              const name =
                so?.name || so?.school_name || so?.display_name || '';
              setSchoolName(name || 'Your School');
            } else {
              setSchoolName('Your School');
            }
          }
        } else {
          setSchoolName('Your School');
        }
      } catch {
        setAdminName('Admin');
        setSchoolName('Your School');
      }
    });

    return () => unsub();
  }, [db]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Check if current user has superadmin permissions
  const isSuperAdmin = () => {
    return currentUserRole === 'superadmin';
  };

  const handleGenerate = async () => {
    if (!adminUid) {
      showSnackbar(
        'Admin not found. Please ensure you are logged in.',
        'error'
      );
      return;
    }

    const teachers = Number(teacherCount || 0);
    const students = Number(studentCount || 0);
    const total = teachers + students;

    if (total <= 0) {
      showSnackbar('Please enter at least one user to generate.', 'error');
      return;
    }

    setGenerating(true);
    try {
      const payload: any = {
        admin_uid: adminUid,
        admin_id: adminUid,
        uid: adminUid,
        total_number: total,
        total: total,
        count: total,
        teacher_count: teachers,
        roles: [
          { role: 'teacher', count: teachers },
          { role: 'student', count: students },
        ],
      };
      if (schoolIdState) payload.school_id = schoolIdState;

      const token = await (authen.currentUser as any)?.getIdToken?.(true);
      const apiUrl = environment?.backendUrl
        ? `${environment.backendUrl.replace(
            /\/$/,
            ''
          )}/api/students/generate-users`
        : '/api/students/generate-users';

      const res = await axios.post(apiUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      // Store the generated users data for download
      const users: any[] = [];
      
      // Add generated students
      if (res.data.generated_students && Array.isArray(res.data.generated_students)) {
        res.data.generated_students.forEach((username: string) => {
          users.push({
            username: username,
            password: username, // Based on your backend, password is same as username
            role: 'student',
            type: 'student'
          });
        });
      }
      
      // Add generated teachers
      if (res.data.generated_teachers && Array.isArray(res.data.generated_teachers)) {
        res.data.generated_teachers.forEach((username: string) => {
          users.push({
            username: username,
            password: username, // Based on your backend, password is same as username
            role: 'teacher',
            type: 'teacher'
          });
        });
      }
      
      // Fallback to successful_usernames if the above arrays are not available
      if (users.length === 0 && res.data.successful_usernames && Array.isArray(res.data.successful_usernames)) {
        res.data.successful_usernames.forEach((username: string) => {
          // We can't determine role from successful_usernames, so we'll use a generic approach
          users.push({
            username: username,
            password: username,
            role: 'user',
            type: 'user'
          });
        });
      }
      
      setGeneratedUsers(users);

      setGenerating(false);
      setOpenGenerateModal(false);
      setSuccessModalOpen(true);
    } catch (err: any) {
      console.error('Error generating users', err);
      setGenerating(false);
      const message = err?.response?.data?.error || err?.message || String(err);
      showSnackbar('Failed to generate user accounts. ' + message, 'error');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'text/csv') {
      setUploadFile(file);
    } else {
      showSnackbar('Please select a valid CSV file', 'error');
      setUploadFile(null);
    }
  };

  const handleDownloadTemplate = () => {
    const rows = [
      ['user_id', 'user_role'],
      ['user001', 'student'],
      ['user002', 'student'],
      ['user003', 'teacher'],
    ];

    const csvContent = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', 'user-roster-template.csv');
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) {
      showSnackbar('Please select a file first', 'error');
      return;
    }

    if (!adminUid) {
      showSnackbar(
        'Admin not found. Please ensure you are logged in.',
        'error'
      );
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('admin_uid', adminUid);
    if (schoolIdState) formData.append('school_id', schoolIdState);

    setUploading(true);
    try {
      const token = await (authen.currentUser as any)?.getIdToken?.(true);
      const apiUrl = environment?.backendUrl
        ? `${environment.backendUrl.replace(
            /\/$/,
            ''
          )}/api/students/bulk-upload`
        : '/api/students/bulk-upload';

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const data = await response.json();
      if (response.ok) {
        // Store the uploaded users data for download
        // Parse the CSV file to get the user data since the backend doesn't return the full user list
        const csvText = await uploadFile.text();
        const lines = csvText.split('\n');
        const users = [];
        
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (line) {
            const [username, role] = line.split(',').map(field => field.replace(/"/g, '').trim());
            if (username && role) {
              users.push({
                username: username,
                password: username, // Based on your backend, password is same as username
                role: role,
                type: role
              });
            }
          }
        }
        
        setUploadedUsers(users);
        
        setUploading(false);
        setOpenUploadModal(false);
        setUploadSuccessModalOpen(true);
        setUploadFile(null);
      } else {
        throw new Error(data.error || 'Failed to process bulk upload');
      }
    } catch (error: any) {
      console.error('Error uploading file:', error);
      setUploading(false);
      const message = error?.message || String(error);
      showSnackbar('Failed to upload user roster. ' + message, 'error');
    }
  };

  const validateAdminForm = (): boolean => {
    if (!adminData.username.trim()) {
      showSnackbar('Username is required', 'error');
      return false;
    }
    if (!adminData.email.trim()) {
      showSnackbar('Email is required', 'error');
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(adminData.email)) {
      showSnackbar('Please enter a valid email address', 'error');
      return false;
    }
    if (adminData.phone && !/^\d{10}$/.test(adminData.phone)) {
      showSnackbar('Phone number must be 10 digits', 'error');
      return false;
    }
    if (!adminData.password.trim() || adminData.password.length < 6) {
      showSnackbar('Password must be at least 6 characters', 'error');
      return false;
    }
    if (adminData.password !== adminData.confirmPassword) {
      showSnackbar('Passwords do not match', 'error');
      return false;
    }
    return true;
  };

  const handleAdminChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAdminData({
      ...adminData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddAdmin = async () => {
    if (!validateAdminForm()) {
      return;
    }

    if (!adminUid) {
      showSnackbar('Admin information not found', 'error');
      return;
    }

    // Check if current user has superadmin permissions
    if (!isSuperAdmin()) {
      showSnackbar('Only Super Administrators can create new administrators', 'error');
      return;
    }

    setAddingAdmin(true);
    let firebaseUser: any = null;

    try {
      console.log('Creating Firebase user for:', adminData.email);
      
      // Use the secondary auth instance - this won't affect the current user's session
      const userCredential = await createUserWithEmailAndPassword(
        adminAuth, // Using adminAuth instead of authen
        adminData.email,
        adminData.password
      );
      firebaseUser = userCredential.user;
      console.log('Firebase user created successfully:', firebaseUser.uid);

      // Send email verification using the secondary auth instance
      await sendEmailVerification(firebaseUser);
      console.log('Email verification sent successfully');

      const newAdminUid = userCredential.user.uid;

      const payload: any = {
        username: adminData.username,
        email: adminData.email,
        phone: adminData.phone,
        password: adminData.password,
        authorized_uid: adminUid,
        requested_uid: newAdminUid,
        role: adminRole,
        admin_role: adminRole,
        user_type: adminRole,
        created_by: adminUid,
      };
      if (schoolIdState) payload.school_id = schoolIdState;

      // Use the PRIMARY auth for getting the current user's token (sam_sepiol's token)
      const currentUser = authen.currentUser;
      const idToken = currentUser ? await currentUser.getIdToken() : null;

      const response = await axios.post(
        `${environment.backendUrl}/api/admin/create`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        // Sign out from the secondary auth instance to clean up
        await adminAuth.signOut();
        
        showSnackbar('Admin successfully added! Email verification sent.', 'success');
        setAdminData({
          username: '',
          email: '',
          phone: '',
          password: '',
          confirmPassword: '',
        });
        setOpenAddAdminModal(false);
      }
    } catch (error: any) {
      // Enhanced error handling with secondary auth cleanup
      console.error('Error in handleAddAdmin:', error);

      if (error.code === 'auth/email-already-in-use') {
        showSnackbar(
          'An account with this email address already exists.',
          'error'
        );
      } else if (error.code === 'auth/weak-password') {
        showSnackbar(
          'Password is too weak. Please choose a stronger password.',
          'error'
        );
      } else if (error.code === 'auth/invalid-email') {
        showSnackbar(
          'Invalid email address format.',
          'error'
        );
      } else if (axios.isAxiosError(error)) {
        const errorData = error.response?.data;

        // Clean up Firebase user if backend call fails
        if (firebaseUser) {
          try {
            // Switch to the user we want to delete, then delete
            await adminAuth.updateCurrentUser(firebaseUser);
            const currentAdminUser = adminAuth.currentUser;
            if (currentAdminUser) {
              await deleteUser(currentAdminUser);
              console.log('Cleaned up Firebase user after backend failure');
            }
          } catch (deleteError) {
            console.error('Failed to clean up Firebase user:', deleteError);
          }
        }

        if (
          errorData?.error === 'Admin with this email already exists' ||
          (errorData?.error === 'Failed to create admin user' &&
            errorData?.details?.includes('EMAIL_EXISTS'))
        ) {
          showSnackbar(
            'An account with this email address already exists.',
            'error'
          );
        } else {
          showSnackbar(
            error.response?.data?.error || 'Failed to add admin',
            'error'
          );
        }
      } else {
        if (firebaseUser) {
          try {
            await adminAuth.updateCurrentUser(firebaseUser);
            const currentAdminUser = adminAuth.currentUser;
            if (currentAdminUser) {
              await deleteUser(currentAdminUser);
              console.log('Cleaned up Firebase user after general error');
            }
          } catch (deleteError) {
            console.error('Failed to clean up Firebase user:', deleteError);
          }
        }
        showSnackbar('An unknown error occurred', 'error');
      }

      try {
        await adminAuth.signOut();
      } catch (signOutError) {
        console.log('Admin auth sign out error (expected if no user signed in):', signOutError);
      }
    } finally {
      setAddingAdmin(false);
    }
  };

  const openSetupVideo = (option?: 'generate' | 'upload') => {
    navigate('/resources');
  };

  const openSetupPDF = (option?: 'generate' | 'upload') => {
    navigate('/resources');
  };

  const downloadGeneratedUsers = () => {
    if (generatedUsers.length === 0) {
      showSnackbar('No generated users data available for download', 'error');
      return;
    }

    // Prepare CSV data
    const csvHeaders = 'Username,Password,Role,Type,School,Generated Date';
    const csvRows = generatedUsers.map(user => [
      user.username || user.user_id || '',
      user.password || '',
      user.role || user.user_role || user.type || '',
      user.type || user.user_type || '',
      schoolName,
      new Date().toLocaleDateString()
    ].map(field => `"${field}"`).join(','));

    const csvContent = [csvHeaders, ...csvRows].join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `generated_users_${schoolName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showSnackbar('Generated users list downloaded successfully!', 'success');
  };

  const downloadUploadedUsers = () => {
    if (uploadedUsers.length === 0) {
      showSnackbar('No uploaded users data available for download', 'error');
      return;
    }

    // Prepare CSV data
    const csvHeaders = 'Username,Role,Type,School,Uploaded Date';
    const csvRows = uploadedUsers.map(user => [
      user.username || user.user_id || '',
      user.role || user.user_role || user.type || '',
      user.type || user.user_type || '',
      schoolName,
      new Date().toLocaleDateString()
    ].map(field => `"${field}"`).join(','));

    const csvContent = [csvHeaders, ...csvRows].join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `uploaded_users_${schoolName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showSnackbar('Uploaded users list downloaded successfully!', 'success');
  };

  return (
    <>
      <AdminNavbar />
      <MainContainer>
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <ModernHeader>
            <HeaderContent>
              <WelcomeSection>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 0.5,
                  }}
                >
                  <StatusDot />
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      color: COLORS.text.primary,
                      fontSize: { xs: '1.75rem', md: '2rem' },
                    }}
                  >
                    Welcome, {adminName}!
                  </Typography>
                </Box>
                
                {/* Role Indicator */}
                {currentUserRole && (
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                    <Chip
                      label={currentUserRole === 'superadmin' ? 'Super Administrator' : 'Administrator'}
                      color={currentUserRole === 'superadmin' ? 'primary' : 'default'}
                      variant="outlined"
                      size="small"
                      sx={{
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        borderWidth: 2,
                        '& .MuiChip-label': {
                          px: 2,
                        },
                      }}
                    />
                  </Box>
                )}
              </WelcomeSection>

              {/* <ActionsSection>
                <CompactButton
                  startIcon={<PlayCircleOutlineIcon />}
                  onClick={openSetupVideo}
                  size="small"
                >
                  Setup Guide
                </CompactButton>
                <CompactButton
                  startIcon={<PictureAsPdfIcon />}
                  onClick={openSetupPDF}
                  size="small"
                >
                  Instructions
                </CompactButton>
              </ActionsSection> */}
            </HeaderContent>
          </ModernHeader>

          {/* Modern Green Tabs */}
          <ResponsiveTabsContainer>
            <ResponsiveStyledTabs
              value={tabValue}
              onChange={handleTabChange}
              variant="standard"
            >
              <ResponsiveStyledTab
                label="Add Users"
                icon={<GroupsIcon />}
                iconPosition="start"
              />
              <ResponsiveStyledTab
                label="Manage Admins"
                icon={<SupervisorAccountIcon />}
                iconPosition="start"
              />
            </ResponsiveStyledTabs>
          </ResponsiveTabsContainer>

          {/* Tab Panel 0: Add Users */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 600,
                  color: COLORS.text.primary,
                  mb: 1,
                  fontSize: '1.5rem',
                }}
              >
                How would you like to add users?
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  color: COLORS.text.secondary,
                  maxWidth: 600,
                  mx: 'auto',
                  fontSize: '1rem',
                  lineHeight: 1.5,
                }}
              >
                Choose the approach that fits your school's workflow and privacy
                preferences.
              </Typography>
            </Box>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Generate Users Card */}
              <Grid item xs={12} md={6}>
                <Grow in={true} timeout={1000}>
                  <StyledCard>
                    <CardContent
                      sx={{
                        p: 3,
                        height: 380,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        sx={{ mb: 2 }}
                      >
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryLight} 100%)`,
                            animation: `${float} 4s ease-in-out infinite`,
                          }}
                        >
                          <SparklesIcon sx={{ fontSize: 28 }} />
                        </Avatar>
                        <RecommendedChip
                          label="Recommended"
                          size="small"
                          icon={
                            <StarIcon
                              sx={{ fontSize: '0.875rem !important' }}
                            />
                          }
                        />
                      </Box>

                      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: COLORS.text.primary,
                            mb: 1.5,
                            fontSize: '1.25rem',
                          }}
                        >
                          Generate New Usernames
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: COLORS.text.secondary,
                            lineHeight: 1.6,
                            mb: 2,
                            fontSize: '0.95rem',
                          }}
                        >
                          Best for privacy and simplicity. We'll create
                          secure, anonymous usernames and passwords for your
                          users.
                        </Typography>

                        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                          <Alert
                            severity="info"
                            sx={{
                              borderRadius: 1,
                              backgroundColor: 'rgba(46, 125, 50, 0.05)',
                              border: '1px solid rgba(46, 125, 50, 0.1)',
                              width: '100%',
                              '& .MuiAlert-icon': {
                                color: COLORS.primary,
                              },
                            }}
                          >
                            <AlertTitle
                              sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.5 }}
                            >
                              Your Role
                            </AlertTitle>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}
                            >
                              We provide usernames. You assign and distribute
                              them to your students.
                            </Typography>
                          </Alert>
                        </Box>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 3, pt: 0 }}>
                      <Stack spacing={2} sx={{ width: '100%' }}>
                        <PrimaryButton
                          fullWidth
                          startIcon={<RocketLaunchIcon />}
                          onClick={() => setOpenGenerateModal(true)}
                        >
                          Generate User Accounts
                        </PrimaryButton>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <CompactButton
                            startIcon={<PlayCircleOutlineIcon />}
                            onClick={() => openSetupVideo('generate')}
                            size="small"
                            sx={{ flex: 1 }}
                          >
                            Setup Guide
                          </CompactButton>
                          <CompactButton
                            startIcon={<PictureAsPdfIcon />}
                            onClick={() => openSetupPDF('generate')}
                            size="small"
                            sx={{ flex: 1 }}
                          >
                            Instructions
                          </CompactButton>
                        </Box>
                      </Stack>
                    </CardActions>
                  </StyledCard>
                </Grow>
              </Grid>

              {/* Upload Users Card */}
              <Grid item xs={12} md={6}>
                <Grow in={true} timeout={1200}>
                  <StyledCard>
                    <CardContent
                      sx={{
                        p: 3,
                        height: 380,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        sx={{ mb: 2 }}
                      >
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            backgroundColor: COLORS.secondary,
                            animation: `${float} 4s ease-in-out infinite 0.5s`,
                          }}
                        >
                          <CloudUploadIcon sx={{ fontSize: 28 }} />
                        </Avatar>
                        <FlexibleChip label="Flexible" size="small" />
                      </Box>

                      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: COLORS.text.primary,
                            mb: 1.5,
                            fontSize: '1.25rem',
                          }}
                        >
                          Bring your Own Usernames
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: COLORS.text.secondary,
                            lineHeight: 1.6,
                            mb: 2,
                            fontSize: '0.95rem',
                          }}
                        >
                          Best for consistency and easy tracking. Upload a CSV
                          with usernames your school already uses.
                        </Typography>

                        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                          <Alert
                            severity="success"
                            sx={{
                              borderRadius: 1,
                              backgroundColor: 'rgba(56, 142, 60, 0.05)',
                              border: '1px solid rgba(56, 142, 60, 0.1)',
                              width: '100%',
                              '& .MuiAlert-icon': {
                                color: COLORS.success,
                              },
                            }}
                          >
                            <AlertTitle
                              sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.5 }}
                            >
                              Pro-Tip
                            </AlertTitle>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}
                            >
                              Many schools use student registration numbers as usernames, as students already know their logins.
                            </Typography>
                          </Alert>
                        </Box>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 3, pt: 0 }}>
                      <Stack spacing={2} sx={{ width: '100%' }}>
                        {/* Use PrimaryButton gradient/color for Upload action to match Pricing design */}
                        <PrimaryButton
                          fullWidth
                          startIcon={<UploadFileIcon />}
                          onClick={() => setOpenUploadModal(true)}
                        >
                          Upload User Roster
                        </PrimaryButton>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <CompactButton
                            startIcon={<PlayCircleOutlineIcon />}
                            onClick={() => openSetupVideo('upload')}
                            size="small"
                            sx={{ flex: 1 }}
                          >
                            Setup Guide
                          </CompactButton>
                          <CompactButton
                            startIcon={<PictureAsPdfIcon />}
                            onClick={() => openSetupPDF('upload')}
                            size="small"
                            sx={{ flex: 1 }}
                          >
                            Instructions
                          </CompactButton>
                        </Box>
                      </Stack>
                    </CardActions>
                  </StyledCard>
                </Grow>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab Panel 1: Manage Admins */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 600,
                  color: COLORS.text.primary,
                  mb: 1,
                  fontSize: '1.5rem',
                }}
              >
                Manage Administrators
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  color: COLORS.text.secondary,
                  maxWidth: 600,
                  mx: 'auto',
                  fontSize: '1rem',
                  lineHeight: 1.5,
                }}
              >
                Add and manage system administrators
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <StyledCard>
                  <CardContent 
                    sx={{ 
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: COLORS.text.primary,
                        mb: 3,
                        fontSize: '1.25rem',
                      }}
                    >
                      Current Administrators
                    </Typography>

                    <List sx={{ py: 0, flexGrow: 1 }}>
                      <ListItem
                        sx={{
                          px: 2,
                          py: 2,
                          borderRadius: 2,
                          backgroundColor: COLORS.hover,
                          border: `1px solid ${COLORS.border}`,
                        }}
                      >
                        <ListItemIcon>
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryLight} 100%)`,
                              mr: 2,
                            }}
                          >
                            <PersonIcon sx={{ fontSize: 24 }} />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: COLORS.text.primary,
                              }}
                            >
                              {adminName}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              sx={{
                                color: COLORS.text.secondary,
                                fontSize: '0.875rem',
                              }}
                            >
                              Primary Administrator (You)
                            </Typography>
                          }
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>

              <Grid item xs={12} lg={6}>
                <StyledCard>
                  <CardContent
                    sx={{
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: COLORS.text.primary,
                        mb: 1.5,
                        fontSize: '1.25rem',
                      }}
                    >
                      Add New Administrator
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: COLORS.text.secondary,
                        lineHeight: 1.6,
                        mb: 3,
                        flexGrow: 1,
                        fontSize: '0.95rem',
                      }}
                    >
                      Grant administrator access to another teacher or staff
                      member to help manage school operations.
                    </Typography>

                    {!isSuperAdmin() && (
                      <Alert
                        severity="warning"
                        sx={{
                          mb: 2,
                          fontSize: '0.875rem',
                          '& .MuiAlert-message': {
                            width: '100%',
                          },
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Administrator Creation Restricted
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Only Super Administrators can create new administrators.
                          Contact your Super Administrator for assistance.
                        </Typography>
                      </Alert>
                    )}

                    <Tooltip
                      title={
                        !isSuperAdmin()
                          ? 'Only Super Administrators can create new administrators'
                          : 'Add a new administrator to help manage school operations'
                      }
                      arrow
                    >
                      <span>
                        <PrimaryButton
                          fullWidth
                          startIcon={<AddIcon />}
                          onClick={() => setOpenAddAdminModal(true)}
                          disabled={!isSuperAdmin()}
                          sx={{
                            opacity: !isSuperAdmin() ? 0.6 : 1,
                            cursor: !isSuperAdmin() ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Add Administrator
                        </PrimaryButton>
                      </span>
                    </Tooltip>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Generate Users Dialog */}
          <ModernDialog
            open={openGenerateModal}
            onClose={() => !generating && setOpenGenerateModal(false)}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
          >
            <Box sx={{ p: 4 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 3 }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, fontSize: '1.25rem' }}
                >
                  Generate User Accounts
                </Typography>
                <IconButton
                  onClick={() => !generating && setOpenGenerateModal(false)}
                  disabled={generating}
                  sx={{
                    color: COLORS.text.light,
                    '&:hover': { backgroundColor: COLORS.hover },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>

              <Stack spacing={3}>
                <InfoCard elevation={0}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 1, color: COLORS.text.primary }}
                  >
                    School: {schoolName}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: COLORS.text.secondary }}
                  >
                    Administrator: {adminName}
                  </Typography>
                </InfoCard>

                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, color: COLORS.text.primary }}
                  >
                    User Configuration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <StyledTextField
                        label="Teachers"
                        type="number"
                        value={teacherCount}
                        onChange={(e) =>
                          setTeacherCount(Number(e.target.value))
                        }
                        inputProps={{ min: 0, max: 500 }}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <StyledTextField
                        label="Students"
                        type="number"
                        value={studentCount}
                        onChange={(e) =>
                          setStudentCount(Number(e.target.value))
                        }
                        inputProps={{ min: 0, max: 2000 }}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <SecondaryButton
                    onClick={() => setOpenGenerateModal(false)}
                    disabled={generating}
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    onClick={handleGenerate}
                    disabled={generating}
                    startIcon={
                      generating ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <RocketLaunchIcon />
                      )
                    }
                  >
                    {generating ? 'Generating...' : 'Generate Users'}
                  </PrimaryButton>
                </Stack>
              </Stack>
            </Box>
          </ModernDialog>

          {/* Upload Users Dialog */}
          <ModernDialog
            open={openUploadModal}
            onClose={() => !uploading && setOpenUploadModal(false)}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
          >
            <Box sx={{ p: 4 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 3 }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, fontSize: '1.25rem' }}
                >
                  Upload User Roster
                </Typography>
                <IconButton
                  onClick={() => !uploading && setOpenUploadModal(false)}
                  disabled={uploading}
                  sx={{
                    color: COLORS.text.light,
                    '&:hover': { backgroundColor: COLORS.hover },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>

              <Stack spacing={3}>
                <InfoCard elevation={0}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 1, color: COLORS.text.primary }}
                  >
                    School: {schoolName}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: COLORS.text.secondary }}
                  >
                    Administrator: {adminName}
                  </Typography>
                </InfoCard>

                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, color: COLORS.text.primary }}
                  >
                    Upload CSV File
                  </Typography>

                  <Button
                    component="label"
                    variant={uploadFile ? 'contained' : 'outlined'}
                    startIcon={<CloudUploadIcon />}
                    disabled={uploading}
                    fullWidth
                    sx={{
                      mb: 3,
                      py: 2,
                      borderRadius: 1.5,
                      ...(uploadFile
                        ? {
                            background: `linear-gradient(135deg, ${COLORS.success} 0%, #2e7d32 100%)`,
                            '&:hover': {
                              background: `linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)`,
                            },
                          }
                        : {
                            borderColor: COLORS.border,
                            color: COLORS.text.primary,
                            '&:hover': {
                              borderColor: COLORS.primary,
                              backgroundColor: COLORS.hover,
                            },
                          }),
                    }}
                  >
                    {uploadFile
                      ? `Selected: ${uploadFile.name}`
                      : 'Select CSV File'}
                    <VisuallyHiddenInput
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                    />
                  </Button>

                  <Box
                    sx={{
                      p: 2.5,
                      backgroundColor: 'rgba(46, 125, 50, 0.05)',
                      borderRadius: 2,
                      border: `2px dashed ${COLORS.border}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1.5,
                        color: COLORS.text.primary,
                        fontWeight: 500,
                      }}
                    >
                      Need the template?
                    </Typography>
                    <Button
                      variant="text"
                      startIcon={<GetAppIcon />}
                      onClick={handleDownloadTemplate}
                      size="small"
                      sx={{
                        color: COLORS.primary,
                        '&:hover': {
                          backgroundColor: 'rgba(46, 125, 50, 0.1)',
                        },
                      }}
                    >
                      Download CSV Template
                    </Button>
                  </Box>
                </Box>

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <SecondaryButton
                    onClick={() => setOpenUploadModal(false)}
                    disabled={uploading}
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    onClick={handleBulkUpload}
                    disabled={!uploadFile || uploading}
                    startIcon={
                      uploading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <CloudUploadIcon />
                      )
                    }
                  >
                    {uploading ? 'Uploading...' : 'Upload Users'}
                  </PrimaryButton>
                </Stack>
              </Stack>
            </Box>
          </ModernDialog>

          {/* Add Admin Dialog */}
          <ModernDialog
            open={openAddAdminModal}
            onClose={() => !addingAdmin && setOpenAddAdminModal(false)}
            TransitionComponent={Transition}
            maxWidth="md" // Changed from "sm" to "md" for better spacing
            fullWidth
          >
            <Box sx={{ p: 4, maxHeight: '90vh', overflow: 'auto' }}>
              {' '}
              {/* Added scroll */}
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}
              >
                Add New Administrator
              </Typography>
              <IconButton
                onClick={() => !addingAdmin && setOpenAddAdminModal(false)}
                disabled={addingAdmin}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: 16,
                  color: COLORS.text.light,
                  '&:hover': { backgroundColor: COLORS.hover },
                }}
              >
                <CloseIcon />
              </IconButton>
              <Stack spacing={3} sx={{ mt: 2 }}>
                {/* School Info */}
                <InfoCard>
                  <Typography
                    variant="body2"
                    sx={{ color: COLORS.text.secondary, mb: 0.5 }}
                  >
                    <strong>School:</strong> {schoolName}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: COLORS.text.secondary }}
                  >
                    <strong>Current Administrator:</strong> {adminName}
                  </Typography>
                </InfoCard>

                {/* Admin Contact Information */}
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, color: COLORS.text.primary }}
                  >
                    Administrator Information
                  </Typography>

                  <Grid container spacing={2}>
                    {/* Full Name */}
                    <Grid item xs={12}>
                      <StyledTextField
                        name="username"
                        label="Full Name"
                        value={adminData.username}
                        onChange={handleAdminChange}
                        fullWidth
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    {/* Email and Phone in same row */}
                    <Grid item xs={12} md={8}>
                      <StyledTextField
                        name="email"
                        label="Email Address"
                        type="email"
                        value={adminData.email}
                        onChange={handleAdminChange}
                        fullWidth
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <StyledTextField
                        name="phone"
                        label="Phone (Optional)"
                        value={adminData.phone}
                        onChange={handleAdminChange}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    {/* Role Selection */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Administrator Role</InputLabel>
                        <Select
                          value={adminRole}
                          label="Administrator Role"
                          onChange={(e: SelectChangeEvent) =>
                            setAdminRole(e.target.value)
                          }
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                            },
                          }}
                          startAdornment={
                            <InputAdornment position="start">
                              <SupervisorAccountIcon />
                            </InputAdornment>
                          }
                        >
                          <MenuItem value="admin">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <PersonIcon fontSize="small" />
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Admin
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Standard admin access with user management
                                </Typography>
                              </Box>
                            </Box>
                          </MenuItem>
                          <MenuItem value="superadmin">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <StarIcon fontSize="small" color="warning" />
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Superadmin
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Extended system access with ability to manage admins
                                </Typography>
                              </Box>
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>

                {/* Admin Password */}
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, color: COLORS.text.primary }}
                  >
                    Account Password
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        name="password"
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        value={adminData.password}
                        onChange={handleAdminChange}
                        fullWidth
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <VpnKeyIcon />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? (
                                  <VisibilityOffIcon />
                                ) : (
                                  <VisibilityIcon />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        name="confirmPassword"
                        label="Confirm Password"
                        type={showPassword ? 'text' : 'password'}
                        value={adminData.confirmPassword}
                        onChange={handleAdminChange}
                        fullWidth
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <VpnKeyIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Note */}
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <AlertTitle>Important Note</AlertTitle>
                  The new administrator will receive an email verification link to activate
                  their account. They must verify their email before they can log in.
                  {adminRole === 'superadmin' && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, fontWeight: 600, color: 'warning.main' }}
                    >
                      ⚠️ Super Administrators have full system access including
                      billing and system settings.
                    </Typography>
                  )}
                </Alert>

                {/* Action Buttons */}
                <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                  <SecondaryButton
                    onClick={() => setOpenAddAdminModal(false)}
                    disabled={addingAdmin}
                    fullWidth
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    onClick={handleAddAdmin}
                    disabled={addingAdmin}
                    fullWidth
                    startIcon={
                      addingAdmin ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <PersonAddAlt1Icon />
                      )
                    }
                  >
                    {addingAdmin
                      ? 'Adding...'
                      : `Add ${
                          adminRole === 'superadmin' ? 'Super ' : ''
                        }Administrator`}
                  </PrimaryButton>
                </Stack>
              </Stack>
            </Box>
          </ModernDialog>

          {/* Success Dialogs */}
          <ModernDialog
            open={successModalOpen}
            onClose={() => setSuccessModalOpen(false)}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
          >
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: `linear-gradient(135deg, ${COLORS.success} 0%, #2e7d32 100%)`,
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 32 }} />
              </Avatar>

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Users Created Successfully!
              </Typography>

              <Typography
                variant="body1"
                sx={{ color: COLORS.text.secondary, mb: 3, lineHeight: 1.5 }}
              >
                Your users' profiles have been created and are ready to use. You
                can now manage them and track their progress.
              </Typography>

              <Stack direction="row" spacing={2} justifyContent="center">
                <SecondaryButton onClick={() => setSuccessModalOpen(false)}>
                  Close
                </SecondaryButton>
                <SecondaryButton
                  onClick={downloadGeneratedUsers}
                  startIcon={<GetAppIcon />}
                >
                  Download List
                </SecondaryButton>
                <PrimaryButton
                  onClick={() => {
                    setSuccessModalOpen(false);
                    navigate('/school-users-management');
                  }}
                  startIcon={<GroupsIcon />}
                >
                  Manage Users
                </PrimaryButton>
              </Stack>
            </Box>
          </ModernDialog>

          <ModernDialog
            open={uploadSuccessModalOpen}
            onClose={() => setUploadSuccessModalOpen(false)}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
          >
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: `linear-gradient(135deg, ${COLORS.success} 0%, #2e7d32 100%)`,
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 32 }} />
              </Avatar>

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Roster Uploaded Successfully!
              </Typography>

              <Typography
                variant="body1"
                sx={{ color: COLORS.text.secondary, mb: 3, lineHeight: 1.5 }}
              >
                Your user roster has been processed and all accounts are now
                ready for classroom use.
              </Typography>

              <Stack direction="row" spacing={2} justifyContent="center">
                <SecondaryButton
                  onClick={() => setUploadSuccessModalOpen(false)}
                >
                  Close
                </SecondaryButton>
                <SecondaryButton
                  onClick={downloadUploadedUsers}
                  startIcon={<GetAppIcon />}
                >
                  Download List
                </SecondaryButton>
                <PrimaryButton
                  onClick={() => {
                    setUploadSuccessModalOpen(false);
                    navigate('/school-users-management');
                  }}
                  startIcon={<GroupsIcon />}
                >
                  View Users
                </PrimaryButton>
              </Stack>
            </Box>
          </ModernDialog>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              variant="filled"
              sx={{ width: '100%', boxShadow: 4 }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </MainContainer>
      <Footer />
    </>
  );
}
